import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
} from "lucide-react";
import { FONT_STYLES, type FontStyle } from "../lib/fontStyles";
import { copyText } from "../lib/copyText";
import {
  buildCopySuccessEvent,
  createOneTimeAnalyticsEvent,
  trackAnalyticsEvent,
} from "../lib/analytics";
import type {
  LocalizedPageContent,
  PageLocale,
} from "../data/spanishPageContent";
import { ENGLISH_TOOL } from "../data/englishTool";
type CopyState = {
  key: string;
  status: "copied" | "failed";
} | null;

type CustomSymbolTarget = "prefix" | "suffix";

type DecorationGroup = {
  id: string;
  label: string;
  templates: string[];
};

const DECORATION_GROUPS: DecorationGroup[] = [
  {
    id: "bow",
    label: "Bow",
    templates: [
      "⋆.˚ 🎀 {text} 🎀 .˚⋆",
      "୨୧ {text} ୨୧",
      "₊˚⊹ ᰔ {text} ᰔ ⊹˚₊",
      "♡₊˚ {text} ୨୧",
      "🎀 · {text} · 🎀",
    ],
  },
  {
    id: "heart",
    label: "Heart",
    templates: [
      "♡ {text} ♡",
      "♡₊˚ {text} ˚₊♡",
      "꒰ა {text} ໒꒱",
      "♡⸝⸝ {text} ⸝⸝♡",
      "『♡』 {text} 『♡』",
    ],
  },
  {
    id: "star",
    label: "Star",
    templates: [
      "✧ {text} ✧",
      "⋆｡°✩ {text} ✩°｡⋆",
      "˚₊‧꒰ა ☆ {text} ☆ ໒꒱ ‧₊˚",
      "✩₊˚.⋆☾⋆⁺₊✧ {text}",
      "♱★ {text} ★♱",
    ],
  },
  {
    id: "soft",
    label: "Soft",
    templates: [
      "𓂃 ࣪˖ {text} ˖ ࣪𓂃",
      "☁︎ {text} ☁︎",
      "˚｡⋆ {text} ⋆｡˚",
      "ʚ₍ᐢ. .ᐢ₎ɞ {text}",
      "❀° ┄──────╮\n{text}\n╰──────┄ °❀",
    ],
  },
  {
    id: "dark",
    label: "Dark",
    templates: [
      "☾ {text} ✦",
      "♱ {text} 𖤐",
      "༒ {text} ༒",
      "╭── ⋅ ⋅ ── ✩\n{text}\n╰── ⋅ ⋅ ── ✩",
      "⋘ {text} ⋙",
    ],
  },
  {
    id: "minimal",
    label: "Minimal",
    templates: [
      "· {text} ·",
      "— {text} —",
      "「 {text} 」",
      "·\n{text}\n·",
      "{text} | · | ✧",
    ],
  },
  {
    id: "korean",
    label: "Korean",
    templates: [
      "˚₊· {text} ·₊˚",
      "♡ ㅤ{text}ㅤ ♡",
      "𓂃 {text} 𓂃",
      "꒰ {text} ꒱ ೀ",
      "｡ﾟ {text} ﾟ｡",
    ],
  },
  {
    id: "loading",
    label: "Loading",
    templates: [
      "{text}\n[■■■■■■■■□□] 80%",
      "▒▒▒▒▒▒▒▒▒▒\n{text}",
      "✦\n{text}\n✦",
      "{text}\n✦ ✦ ✦",
    ],
  },
  {
    id: "kaomoji",
    label: "Kaomoji",
    templates: [
      "♡ ∩_∩\n（„• ֊ •„)♡\n{text}",
      "/) /)\n( •-• ) ♡ {text}",
      "꒰ᐢ. .ᐢ꒱₊˚⊹ {text}",
      "( ˘ᴗ˘ )\n{text}",
    ],
  },
  {
    id: "name-art",
    label: "Name Art",
    templates: [
      "୧ ✦ {text} ◇ ʚ",
      "𐂂 ⎯⎯ {text} ๑ ❀",
      "✣ ˚⌁ 🧸 {text} 〰 ✿",
      "໒ □ {text} // 🥕 ◡̈",
      "↝ 🌸 # {text} ⌁",
      "✩ // ˚₊ {text} 🕊 𐂂",
      "`⌁ ︵ {text} ︶",
      "· ʚ {text} ᘛᘚ !",
      "✣ ๑▸ # {text} ˙ ◎",
      "₊ 🎀 ↝ `{text}! ◎ ₊ᘛᘚ !",
      "˘ ᵕ ˘ ⋆ ㄚ › {text} ᘏ ₊ᘛᘚ !",
      "· ᵕ ▷ ︵ {text} ˏˎ",
    ],
  },
  {
    id: "scrapbook",
    label: "Scrapbook",
    templates: [
      '☽ 🐇 " {text} 🎀 ⌒ ˙',
      "🩰 ʚ｡ {text} ★ 〰",
      "〰 ˚ {text} 🌸 ∩⋈∩",
      "🥛 ₊ ‹ {text} ✦ しɔ",
      "▰ ⋆ ˙ {text} 🍳 ˎ ‹",
      "- ʚ!! 🌻 [ {text} ] ◎´",
      "- ᨳ # [ {text} ]",
      "- ♱ 💌 ❝ [ {text} ] ❞",
      "- 〃 ☁️ ❝ 🍓 - [ {text} ]",
      "- 🫙 ʚ❞ {text}",
      "- // 🐚 ƐƷ {text}",
    ],
  },
  {
    id: "soft-notes",
    label: "Soft Notes",
    templates: [
      "👜 ˚ 🧸 Ꮺ ☁️\n{text}",
      "✣˚ {text}\n✣˚",
      "☁️ * ✧ ୨ {text} ୧ ✧ * ☁️",
      "♡ : 🍓 🥛 ☁️ ' ୨\n{text}\n『 ♡ 』",
      "⋱⋰⋱ 🫙 ˚☆🌷🧸 ᏪᏪᏪ ˚\nƐƷ {text} ƐƷ\n⋱⋰⋱ 🎁 ⋱⋰⋱",
      "✎...(▭) Ɛ {text} 3\n( ˙ᵕ˙♡) ᨳ 🥛 ❧",
    ],
  },
  {
    id: "blue-mood",
    label: "Blue Mood",
    templates: [
      "♡, 💦 : {text} . ( • ᴗ • )",
      "· ☆ · {text}, ☁️ · ♡",
      "♡, 🌊 · {text} : ↻",
      "▱ : {text}, 🌐 · ♡̸",
      "!, ✦, {text}, ˘ᵕ˘",
      "♡ . — : ♡ : — . ♡",
    ],
  },
  {
    id: "angel",
    label: "Angel",
    templates: [
      "☆{text}☆",
      "₊ ˚ ᨳ☆ ˚ 🪽 ִִ ᰔ",
      "* 𓂃 ♡ ,, 🪐 ˚",
      "♡⁺ 🦢 · ₊✧",
      "˚ ୨ 🌷 ᨳ ˚ 🎀 ˟",
      "૮꒰ ˶• ༝ •˶꒱ა ♡",
      "꒰ა 𓂃 ໒꒱",
      "⋱˚* ︵ 𓍯✧✩✧ ︵ ˚*⋰",
      "｡ 🫧 ⋆˙𖦹 *:･｡",
      "🫧 ☆ 𓂃⋆☆ ˚₊ 🌐",
      "· | ✦ ʚ♡ɞ ✦ | ·",
    ],
  },
];

const SYMBOL_CHOICES = [
  "♡",
  "✧",
  "୨୧",
  "˚₊‧",
  "♡₊˚",
  "⋆｡°✩",
  "☾",
  "𖤐",
  "♱",
  "༒",
  "𓂃 ࣪˖",
  "☁︎",
  "✿",
  "❀",
  "ʚ₍ᐢ. .ᐢ₎ɞ",
  "♡⸝⸝",
  "▮▮▮▯▯",
  "▒▒▒",
  "◇",
  "〰",
  "🧸",
  "🌸",
  "🕊",
  "🎀",
  "🐇",
  "🩰",
  "🥛",
  "☁️",
  "🌊",
  "💦",
  "🌐",
  "🪐",
  "🌙",
  "🪽",
  "🫧",
];

const applyDecoration = (template: string, text: string) => {
  return template.includes("{text}")
    ? template.split("{text}").join(text)
    : `${template} ${text}`.trim();
};

const buildStyledTemplate = (
  template: string,
  text: string,
  font: FontStyle,
) => {
  const styledText = font.transform(text);
  return applyDecoration(template, styledText);
};

function ScrollStrip({
  children,
  className,
  label,
  locale,
  selected,
}: {
  children: ReactNode;
  className: string;
  label: string;
  locale: PageLocale;
  selected?: string;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  useEffect(() => {
    const node = viewport.current;
    if (!node) return;
    const update = () =>
      setEdges({
        start: node.scrollLeft <= 2,
        end: node.scrollLeft + node.clientWidth >= node.scrollWidth - 2,
      });
    const observer = new ResizeObserver(update);
    observer.observe(node);
    node.addEventListener("scroll", update, { passive: true });
    update();
    return () => {
      observer.disconnect();
      node.removeEventListener("scroll", update);
    };
  }, []);
  useEffect(() => {
    const node = viewport.current;
    const active = node?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!node || !active) return;
    const parent = node.getBoundingClientRect();
    const child = active.getBoundingClientRect();
    if (child.left < parent.left || child.right > parent.right) {
      node.scrollLeft += child.left - parent.left - 2;
    }
  }, [selected]);
  const move = (direction: number) => {
    const node = viewport.current;
    if (node)
      node.scrollBy({
        left: direction * node.clientWidth * 0.7,
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
  };
  return (
    <div className="scroll-strip">
      <button
        type="button"
        className="scroll-arrow"
        disabled={edges.start}
        aria-label={`${locale === "es" ? "Anterior" : "Previous"}: ${label}`}
        onClick={() => move(-1)}
      >
        <ChevronLeft size={18} />
      </button>
      <div ref={viewport} className={className} aria-label={label}>
        {children}
      </div>
      <button
        type="button"
        className="scroll-arrow"
        disabled={edges.end}
        aria-label={`${locale === "es" ? "Siguiente" : "Next"}: ${label}`}
        onClick={() => move(1)}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

export type BioTemplateSelection = { text: string; key: number };
export function BioMaker({
  labels = ENGLISH_TOOL,
  locale = "en",
  selection,
  multiline = false,
  controlHeadings = true,
  cssActionLabels = false,
}: {
  labels?: LocalizedPageContent["tool"];
  locale?: PageLocale;
  selection?: BioTemplateSelection;
  multiline?: boolean;
  controlHeadings?: boolean | "h3";
  cssActionLabels?: boolean;
}) {
  const content = { tool: labels };
  const ControlTitle = controlHeadings === "h3" ? "h3" : controlHeadings ? "h2" : "div";
  const TextInput = multiline ? "textarea" : "input";
  const decorationList = useRef<HTMLDivElement>(null);
  const previewMeasure = useRef<HTMLDivElement>(null);
  const previewInput = useRef<HTMLTextAreaElement>(null);
  const [baseText, setBaseText] = useState("");
  const [fontId, setFontId] = useState("script");
  const [decorationGroupId, setDecorationGroupId] = useState("bow");
  const [template, setTemplate] = useState(DECORATION_GROUPS[0].templates[0]);
  const [customPrefix, setCustomPrefix] = useState("");
  const [customSuffix, setCustomSuffix] = useState("");
  const [customSymbolTarget, setCustomSymbolTarget] =
    useState<CustomSymbolTarget>("prefix");
  const [previewText, setPreviewText] = useState("");
  const [copyState, setCopyState] = useState<CopyState>(null);
  const trackToolStarted = useMemo(
    () =>
      createOneTimeAnalyticsEvent("tool_started", {
        page_language: locale,
      }),
    [locale],
  );

  useEffect(() => {
    if (!selection) return;
    setBaseText(selection.text);
    setFontId("serif");
    setTemplate("{text}");
    setDecorationGroupId("minimal");
    setCustomPrefix("");
    setCustomSuffix("");
    setCopyState(null);
    document
      .getElementById("generator")
      ?.scrollIntoView({ block: "start", behavior: "instant" });
    document.getElementById("bio-text")?.focus({ preventScroll: true });
  }, [selection]);
  const activeFont =
    FONT_STYLES.find((font) => font.id === fontId) ?? FONT_STYLES[0];
  const activeGroup =
    DECORATION_GROUPS.find((group) => group.id === decorationGroupId) ??
    DECORATION_GROUPS[0];

  useEffect(() => {
    const input = multiline ? baseText : baseText.trim();
    const templated = buildStyledTemplate(template, input, activeFont);
    const decorated = [customPrefix.trim(), templated, customSuffix.trim()]
      .filter(Boolean)
      .join(" ");
    setPreviewText(decorated);
    setCopyState(null);
  }, [activeFont, baseText, customPrefix, customSuffix, template, multiline]);

  useEffect(() => {
    const measure = previewMeasure.current;
    const input = previewInput.current;
    if (!measure || !input) return;
    const fitPreview = () => {
      const textHeight = measure.getBoundingClientRect().height;
      const styles = getComputedStyle(input);
      const borders =
        parseFloat(styles.borderTopWidth) +
        parseFloat(styles.borderBottomWidth);
      const height = Math.max(
        parseFloat(styles.minHeight),
        Math.ceil(textHeight + 24 + borders),
      );
      input.style.height = `${height}px`;
      const padding = Math.max(12, (height - borders - textHeight) / 2);
      input.style.paddingBlock = `${padding}px`;
    };
    const observer = new ResizeObserver(fitPreview);
    observer.observe(measure);
    observer.observe(input);
    fitPreview();
    return () => observer.disconnect();
  }, [previewText]);

  useEffect(() => {
    if (decorationList.current) decorationList.current.scrollTop = 0;
  }, [decorationGroupId]);

  const handleGroupChange = (group: DecorationGroup) => {
    setDecorationGroupId(group.id);
    setTemplate(group.templates[0]);
    trackAnalyticsEvent("template_selected", {
      template_group: group.id,
      selection_source: "group",
      page_language: locale,
    });
  };

  const handleRandomStyle = () => {
    const randomGroup =
      DECORATION_GROUPS[Math.floor(Math.random() * DECORATION_GROUPS.length)];
    const randomTemplate =
      randomGroup.templates[
        Math.floor(Math.random() * randomGroup.templates.length)
      ];
    setDecorationGroupId(randomGroup.id);
    setTemplate(randomTemplate);
    trackAnalyticsEvent("template_selected", {
      template_group: randomGroup.id,
      selection_source: "random",
      page_language: locale,
    });
  };

  const addCustomSymbol = (symbol: string) => {
    if (customSymbolTarget === "prefix") {
      setCustomPrefix((current) =>
        [current.trim(), symbol].filter(Boolean).join(" "),
      );
      return;
    }

    setCustomSuffix((current) =>
      [current.trim(), symbol].filter(Boolean).join(" "),
    );
  };

  const handleCopy = async (
    key: string,
    text: string,
    copySource: "preview" | "platform_example" | "style_example",
    exampleGroup?: string,
  ) => {
    try {
      await copyText(text);
      setCopyState({ key, status: "copied" });

      if (copySource === "preview") {
        const event = buildCopySuccessEvent({
          copySource,
          inputText: baseText,
          outputText: text,
          fontStyle: fontId,
          templateGroup: decorationGroupId,
          pageLanguage: locale,
        });
        trackAnalyticsEvent(event.eventName, event.parameters);
      } else {
        const event = buildCopySuccessEvent({
          copySource,
          outputText: text,
          exampleGroup,
          pageLanguage: locale,
        });
        trackAnalyticsEvent(event.eventName, event.parameters);
      }
    } catch {
      setCopyState({ key, status: "failed" });
      trackAnalyticsEvent("copy_failed", {
        copy_source: copySource,
        page_language: locale,
      });
    } finally {
      window.setTimeout(() => setCopyState(null), 2200);
    }
  };

  return (
    <section
      id="generator"
      className="tool-card"
      aria-labelledby="generator-heading"
    >
      <div className="editor-layout">
        <div className="editor-controls">
          <section
            className="editor-section"
            aria-label={content.tool.inputAriaLabel}
          >
            <div className="editor-search">
              <TextInput
                aria-label={content.tool.inputAriaLabel}
                id="bio-text"
                data-clarity-mask="true"
                value={baseText}
                maxLength={multiline ? undefined : 64}
                {...(multiline ? { rows: 3 } : {})}
                onChange={(event) => {
                  const nextText = event.target.value;
                  setBaseText(nextText);

                  if (nextText.trim()) {
                    trackToolStarted();
                  }
                }}
                placeholder={content.tool.inputPlaceholder}
              />
            </div>
          </section>
          <section className="editor-section" aria-labelledby="font-heading">
            <div className="step-title-row">
              <span>1</span>
              <ControlTitle className={controlHeadings === true ? undefined : "tool-control-title"} id="font-heading">{content.tool.fontHeading}</ControlTitle>
            </div>
            <ScrollStrip
              className="font-grid"
              label={content.tool.fontHeading}
              locale={locale}
              selected={fontId}
            >
              {FONT_STYLES.map((font) => (
                <button
                  key={font.id}
                  type="button"
                  aria-pressed={font.id === fontId}
                  className={
                    font.id === fontId ? "font-option active" : "font-option"
                  }
                  onClick={() => {
                    setFontId(font.id);
                    trackAnalyticsEvent("font_selected", {
                      font_style: font.id,
                      page_language: locale,
                    });
                  }}
                >
                  {font.sample}
                </button>
              ))}
            </ScrollStrip>
          </section>

          <section className="editor-section" aria-labelledby="decor-heading">
            <div className="section-title-row template-title-row">
              <div className="step-title-row">
                <span>2</span>
                <ControlTitle className={controlHeadings === true ? undefined : "tool-control-title"} id="decor-heading">{content.tool.templateHeading}</ControlTitle>
              </div>
              <button
                type="button"
                className="random-button"
                aria-label={content.tool.random}
                onClick={handleRandomStyle}
              >
                <RefreshCw size={15} />
                {cssActionLabels ? <span className="css-action-label" data-label={content.tool.random} aria-hidden="true" /> : content.tool.random}
              </button>
            </div>
            <ScrollStrip
              className="style-grid"
              label={content.tool.templateHeading}
              locale={locale}
              selected={decorationGroupId}
            >
              {DECORATION_GROUPS.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  aria-pressed={group.id === decorationGroupId}
                  className={
                    group.id === decorationGroupId
                      ? "style-chip active"
                      : "style-chip"
                  }
                  onClick={() => handleGroupChange(group)}
                >
                  {content.tool.decorationLabels?.[group.id] ?? group.label}
                </button>
              ))}
            </ScrollStrip>

            <div
              ref={decorationList}
              className="decoration-list"
              data-clarity-mask="true"
            >
              {activeGroup.templates.map((item) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={item === template}
                  className={
                    item === template
                      ? "decoration-row active"
                      : "decoration-row"
                  }
                  onClick={() => {
                    setTemplate(item);
                    trackAnalyticsEvent("template_selected", {
                      template_group: activeGroup.id,
                      selection_source: "template",
                      page_language: locale,
                    });
                  }}
                >
                  <span>
                    {buildStyledTemplate(
                      item,
                      baseText.trim() || content.tool.emptyPreviewText,
                      activeFont,
                    )}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="editor-section" aria-labelledby="custom-heading">
            <div className="step-title-row">
              <span>3</span>
              <ControlTitle className={controlHeadings === true ? undefined : "tool-control-title"} id="custom-heading">{content.tool.symbolsHeading}</ControlTitle>
            </div>
            <ScrollStrip
              className="symbol-picker"
              label={content.tool.symbolsHeading}
              locale={locale}
            >
              {SYMBOL_CHOICES.map((symbol) => (
                <button
                  key={symbol}
                  type="button"
                  className="symbol-token"
                  onClick={() => addCustomSymbol(symbol)}
                >
                  {symbol}
                </button>
              ))}
            </ScrollStrip>
            <div className="custom-decoration-grid">
              <label
                className={
                  customSymbolTarget === "prefix" ? "active-target" : undefined
                }
              >
                <span>{content.tool.beforeLabel}</span>
                <input
                  className="custom-input"
                  data-clarity-mask="true"
                  value={customPrefix}
                  onFocus={() => setCustomSymbolTarget("prefix")}
                  onChange={(event) => setCustomPrefix(event.target.value)}
                  placeholder={content.tool.beforePlaceholder}
                />
              </label>
              <label
                className={
                  customSymbolTarget === "suffix" ? "active-target" : undefined
                }
              >
                <span>{content.tool.afterLabel}</span>
                <input
                  className="custom-input"
                  data-clarity-mask="true"
                  value={customSuffix}
                  onFocus={() => setCustomSymbolTarget("suffix")}
                  onChange={(event) => setCustomSuffix(event.target.value)}
                  placeholder={content.tool.afterPlaceholder}
                />
              </label>
            </div>
          </section>
        </div>
      </div>

      <div className="preview-bottom">
        <section className="preview-panel" aria-labelledby="preview-heading">
          <div className="step-title-row">
            <span aria-hidden="true">4</span>
            <ControlTitle className={controlHeadings === true ? undefined : "tool-control-title"} id="preview-heading">{content.tool.previewHeading}</ControlTitle>
          </div>
          <div className="preview-field">
            <div
              ref={previewMeasure}
              className="preview-measure"
              aria-hidden="true"
              data-clarity-mask="true"
            >
              {previewText + "\u200b"}
            </div>
            <textarea
              ref={previewInput}
              aria-label={content.tool.previewHeading}
              data-clarity-mask="true"
              value={previewText}
              onChange={(event) => {
                setPreviewText(event.target.value);
                setCopyState(null);
              }}
              rows={2}
            />
          </div>
        </section>
        <button
          className="copy-wide-button"
          type="button"
          aria-label={cssActionLabels && copyState?.key === "preview" && copyState.status === "copied" ? content.tool.copied : content.tool.copy}
          onClick={() => handleCopy("preview", previewText, "preview")}
        >
          {copyState?.key === "preview" && copyState.status === "copied" ? (
            <Check size={17} />
          ) : (
            <Copy size={17} />
          )}
          {cssActionLabels ? (
            <span className="css-action-label" aria-hidden="true" data-label={copyState?.key === "preview" && copyState.status === "copied" ? content.tool.copied : content.tool.copy} />
          ) : (
            copyState?.key === "preview" && copyState.status === "copied" ? content.tool.copied : content.tool.copy
          )}
        </button>
      </div>
      {copyState?.status === "failed" && (
        <p role="status">
          {locale === "es"
            ? "No se pudo copiar. Selecciona el texto de la vista previa y cópialo manualmente."
            : "Copy didn’t work. Select the preview text and copy it manually."}
        </p>
      )}
    </section>
  );
}
